import { Dropdown } from './dropdown';
import { permissionCodes } from '@snaphire/contracts';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { paths } from '@snaphire/api-client';
import { allRows, useAccess } from './api/data';
import { command, field, name, type GetPath, type Row } from './api/client';
import { operationSchemas, operationParameters } from './api/forms.generated';
import { fieldLabel as label, statusLabel as human, permissionLabel } from './i18n';
import { Button, ErrorState, Field, Loading, Overlay } from './ui';
import { permittedWorkAreas } from './work-areas';
import {
  employeeAccessTargets,
  organizationAccessTarget,
  employeeStatusLabels,
  employeeStatusLabel,
  projectStatusLabels,
} from './hr-permissions';
import { countryChoices, defaultCountryTimezone, timezoneChoices } from './location-options';

export type FormSchema = {
  title?: string;
  const?: string | number | boolean;
  type?: string | string[];
  format?: string;
  nullable?: boolean;
  enum?: (string | number | boolean)[];
  enumLabels?: Record<string, string>;
  properties?: Record<string, FormSchema>;
  required?: string[];
  items?: FormSchema;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  pattern?: string;
  additionalProperties?: boolean | FormSchema;
  variants?: FormSchema[];
  discriminator?: string;
};
export type Action = {
  operation?: string;
  label: string;
  method?: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: keyof paths;
  params?: Record<string, string>;
  initial?: Record<string, unknown>;
  permission?: string;
  requiredPermissions?: string[];
  schemaOverrides?: Record<string, FormSchema>;
  processingOptions?: { source: string; purpose: string; jurisdiction: string }[];
  description?: string;
  choices?: Record<string, Row[]>;
  generatedParameters?: string[];
  hiddenFields?: string[];
};
export function canPerformAction(action: Action, can: (permission: string) => boolean) {
  return (
    (!action.permission || can(action.permission)) && (action.requiredPermissions || []).every(can)
  );
}
export { fieldLabel as label, statusLabel as human } from './i18n';
const relationPaths: Record<string, GetPath> = {
  organizationId: '/api/v1/organizations',
  departmentId: '/api/v1/departments',
  primaryTeamId: '/api/v1/teams',
  additionalTeamIds: '/api/v1/teams',
  positionId: '/api/v1/positions' as GetPath,
  jobFunctionId: '/api/v1/job-functions',
  specialisationId: '/api/v1/specialisations',
  seniorityId: '/api/v1/seniorities',
  minSeniorityId: '/api/v1/seniorities',
  maxSeniorityId: '/api/v1/seniorities',
  locationId: '/api/v1/locations',
  locationIds: '/api/v1/locations',
  applicationId: '/api/v1/applications',
  candidateId: '/api/v1/candidates',
  primaryCandidateId: '/api/v1/candidates',
  mergedCandidateIds: '/api/v1/candidates',
  jobPostingId: '/api/v1/job-postings',
  requisitionIds: '/api/v1/requisitions',
  headcountAllocationIds: '/api/v1/headcount-allocations',
  permissionGroupId: '/api/v1/permission-groups',
  permissionGroupIds: '/api/v1/permission-groups',
  templateVersionId: '/api/v1/process-templates',
  privacyNoticeVersionId: '/api/v1/privacy-notice-versions',
  noticeVersionId: '/api/v1/privacy-notice-versions',
  processingBasisPolicyVersionId: '/api/v1/processing-basis-policy-versions',
  roleProfileVersionId: '/api/v1/role-profiles',
  profileVersionId: '/api/v1/role-profiles',
  skillVersionId: '/api/v1/skills',
  evaluationTemplateVersionId: '/api/v1/employee-evaluation-templates',
  cycleId: '/api/v1/performance-cycles',
  skillId: '/api/v1/skills',
  roleProfileId: '/api/v1/role-profiles',
  seniorityIds: '/api/v1/seniorities',
  reviewerAccountId: '/api/v1/accounts',
  policyId: '/api/v1/retention-policies',
  specialisationIds: '/api/v1/specialisations',
};
const entityPaths: Record<string, GetPath> = {
  Organization: '/api/v1/organizations',
  ORGANIZATION: '/api/v1/organizations',
  Department: '/api/v1/departments',
  DEPARTMENT: '/api/v1/departments',
  Team: '/api/v1/teams',
  TEAM: '/api/v1/teams',
  Employee: '/api/v1/employees',
  Account: '/api/v1/accounts',
  Candidate: '/api/v1/candidates',
  Application: '/api/v1/applications',
  JobPosting: '/api/v1/job-postings',
  Requisition: '/api/v1/requisitions',
};
function targetPaths(value: unknown): GetPath[] {
  if (Array.isArray(value)) return value.flatMap(targetPaths);
  if (!value || typeof value !== 'object') return [];
  const row = value as Record<string, unknown>;
  const path = entityPaths[String(row.scope || row.entityType || row.targetEntityType || '')];
  return [...(path ? [path] : []), ...Object.values(row).flatMap(targetPaths)];
}
const employeeKeys = new Set([
  'employeeId',
  'assigneeEmployeeId',
  'ownerEmployeeId',
  'managerEmployeeId',
  'lineManagerId',
  'functionalManagerId',
  'buddyId',
  'managerId',
  'hrOwnerId',
  'recruiterId',
  'hiringManagerIds',
  'interviewerIds',
]);
export function relation(key: string): GetPath | undefined {
  return employeeKeys.has(key) ? '/api/v1/employees' : relationPaths[key];
}
function nullable(schema: FormSchema) {
  return schema.nullable || (Array.isArray(schema.type) && schema.type.includes('null'));
}
function selectedSchema(
  schema: FormSchema,
  value: Record<string, unknown>,
  editing = false,
): FormSchema {
  if (!schema.variants || !schema.discriminator) return schema;
  const key = schema.discriminator;
  const variant =
    schema.variants.find((item) => item.properties?.[key]?.enum?.includes(value[key] as string)) ||
    schema.variants[0]!;
  return editing
    ? {
        ...variant,
        properties: {
          ...variant.properties,
          [key]: {
            type: 'string',
            enum: schema.variants.flatMap((item) => item.properties?.[key]?.enum || []),
          },
        },
      }
    : variant;
}
export function defaults(
  schema: FormSchema,
  initial: Record<string, unknown> = {},
): Record<string, unknown> {
  schema = selectedSchema(schema, initial);
  return Object.fromEntries(
    Object.entries(schema.properties || {})
      .filter(([key]) => key in initial || schema.required?.includes(key))
      .map(([key, item]) => [
        key,
        initial[key] ??
          (key === 'timezone'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : key === 'schemaVersion'
              ? 1
              : ['key', 'taskKey', 'id'].includes(key)
                ? item.pattern?.startsWith('^[a-z]')
                  ? `question_${crypto.randomUUID().replaceAll('-', '_')}`
                  : crypto.randomUUID()
                : undefined) ??
          item.const ??
          (key === 'version'
            ? initial.version
            : nullable(item)
              ? null
              : item.type === 'array'
                ? []
                : item.type === 'boolean'
                  ? false
                  : item.type === 'object'
                    ? defaults(item)
                    : (item.enum?.[0] ?? '')),
      ]),
  );
}
export function formBody(
  schema: FormSchema,
  value: Record<string, unknown>,
): Record<string, unknown> {
  schema = selectedSchema(schema, value);
  return Object.fromEntries([
    ...(schema.additionalProperties
      ? Object.entries(value).filter(([key]) => !schema.properties?.[key])
      : []),
    ...Object.entries(schema.properties || {}).flatMap(([key, item]) => {
      let current = value[key];
      if (current === null && !schema.required?.includes(key) && !nullable(item)) return [];
      if (current === undefined || (current === '' && !schema.required?.includes(key))) return [];
      if (current === '' && nullable(item)) current = null;
      if (
        key === 'scopeEntityId' &&
        ['WORKSPACE', 'SELF', 'DIRECT_REPORTS', 'ASSIGNED_ENTITIES'].includes(String(value.scope))
      )
        current = null;
      if (item.format === 'date-time' && typeof current === 'string' && current)
        current = new Date(current).toISOString();
      const type = Array.isArray(item.type) ? item.type.find((type) => type !== 'null') : item.type;
      if ((type === 'number' || type === 'integer') && typeof current === 'string')
        current = Number(current);
      if (item.properties && current && typeof current === 'object' && !Array.isArray(current))
        current = formBody(item, current as Record<string, unknown>);
      if (type === 'array' && Array.isArray(current) && item.items?.properties)
        current = current.map((row) => formBody(item.items!, row as Record<string, unknown>));
      return [[key, current]];
    }),
  ]);
}
function pathsFor(schema: FormSchema): GetPath[] {
  return [
    ...new Set(
      Object.entries(schema.properties || {}).flatMap(([key, item]) => {
        const path = relation(key);
        if (['jobFunctionId', 'specialisationId', 'specialisationIds'].includes(key)) return [];
        return [
          ...(path ? [path] : []),
          ...pathsFor(item),
          ...(item.items ? pathsFor(item.items) : []),
          ...(item.variants?.flatMap(pathsFor) || []),
        ];
      }),
    ),
  ];
}
function ChoicePicker({
  id,
  title,
  options,
  value,
  multiple,
  onChange,
  optional,
}: {
  id: string;
  title: string;
  options: { id: string; title: string }[];
  value: unknown;
  multiple: boolean;
  optional: boolean;
  onChange: (value: unknown) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [invalid, setInvalid] = useState(false);
  const selected = Array.isArray(value)
    ? value.map(String)
    : value === null || value === undefined || value === ''
      ? []
      : [String(value)];
  return (
    <div className="choice-picker">
      <Dropdown
        options={[
          ...(optional && !multiple ? [{ id: '', label: 'Не выбрано' }] : []),
          ...options.map((option) => ({ id: option.id, label: option.title })),
        ]}
        selected={selected}
        label={title}
        id={id}
        multiple={multiple}
        className="choice-trigger"
        required={!optional}
        invalid={invalid}
        buttonRef={buttonRef}
        placeholder={options.length ? 'Выберите значение' : 'Нет доступных записей'}
        onChange={(next) => {
          setInvalid(false);
          onChange(multiple ? next : next[0] || null);
        }}
      />
      {!multiple && !optional && (
        <input
          name={id}
          className="choice-validation"
          aria-hidden="true"
          tabIndex={-1}
          required
          value={selected[0] || ''}
          onChange={() => {}}
          onInvalid={(event) => {
            event.preventDefault();
            setInvalid(true);
            buttonRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}

function Input({
  id,
  schema,
  value,
  onChange,
  required,
  choices,
  parent,
}: {
  id: string;
  schema: FormSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  required: boolean;
  choices: Record<string, Row[]>;
  parent: Record<string, unknown>;
}) {
  const key = id.split('.').at(-1)!;
  const title = schema.title || label(key);
  if (['jobFunctionId', 'specialisationId', 'specialisationIds'].includes(key)) return null;
  const path = relation(key);
  let options =
    key === 'assignmentId'
      ? choices.assignments || []
      : key === 'copyFromVersionId'
        ? choices.versions || []
        : key === 'toStageId'
          ? choices.stages || []
          : choices[key]
            ? choices[key]
            : path
              ? choices[path] || []
              : [];
  if (['entityId', 'scopeEntityId', 'targetEntityId'].includes(key)) {
    const target = String(parent.scope || parent.entityType || parent.targetEntityType || '');
    options = choices[entityPaths[target] || 'workspace'] || [];
  }
  if (
    key === 'scopeEntityId' &&
    ['WORKSPACE', 'SELF', 'DIRECT_REPORTS', 'ASSIGNED_ENTITIES'].includes(String(parent.scope))
  )
    return null;
  if (key === 'departmentId' && parent.organizationId)
    options = options.filter((row) => row.organizationId === parent.organizationId);
  if (['departmentId', 'positionId', 'seniorityId', 'locationId'].includes(key))
    options = options.filter((row) => !row.status || row.status === 'ACTIVE' || row.id === value);
  if (['primaryTeamId', 'additionalTeamIds'].includes(key)) {
    if (parent.organizationId)
      options = options.filter(
        (row) => !row.organizationId || row.organizationId === parent.organizationId,
      );
    options = options.filter(
      (row) =>
        row.status === 'ACTIVE' ||
        row.id === value ||
        (Array.isArray(value) && value.includes(row.id)),
    );
    if (key === 'additionalTeamIds')
      options = options.filter((row) => row.id !== parent.primaryTeamId);
  }
  if (key === 'seniorityIds') options = options.filter((row) => row.status === 'ACTIVE');
  const type = Array.isArray(schema.type)
    ? schema.type.find((value) => value !== 'null')
    : schema.type;
  const optional = !required || nullable(schema);
  if (
    schema.const !== undefined ||
    ['schemaVersion', 'expectedEmployeeVersion', 'key', 'taskKey'].includes(key) ||
    (key === 'id' && id.includes('.'))
  )
    return null;
  if (
    type === 'object' &&
    schema.additionalProperties &&
    (!schema.properties || !Object.keys(schema.properties).length)
  ) {
    const data = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
    return (
      <fieldset className="form-section">
        <legend>{title}</legend>
        {Object.entries(data).map(([name, current], index) => (
          <div className="parameter-row" key={index}>
            <Field
              label="Название"
              value={name}
              onChange={(event) =>
                onChange(
                  Object.fromEntries(
                    Object.entries(data).map(([entry, item]) => [
                      entry === name ? event.target.value : entry,
                      item,
                    ]),
                  ),
                )
              }
            />
            <Field
              label="Значение"
              value={String(current ?? '')}
              onChange={(event) => onChange({ ...data, [name]: event.target.value })}
            />
            <Button
              icon="Cross"
              aria-label="Убрать параметр"
              onClick={() =>
                onChange(
                  Object.fromEntries(Object.entries(data).filter(([entry]) => entry !== name)),
                )
              }
            />
          </div>
        ))}
        <Button
          icon="Plus"
          onClick={() => onChange({ ...data, [`Параметр ${Object.keys(data).length + 1}`]: '' })}
        >
          Добавить параметр
        </Button>
      </fieldset>
    );
  }
  if (type === 'object' && schema.properties) {
    const data =
      value && typeof value === 'object' ? (value as Record<string, unknown>) : defaults(schema);
    return (
      <fieldset className="form-section">
        <legend>{title}</legend>
        {optional && (
          <label className="form-check">
            <input
              type="checkbox"
              name={`${id}-enabled`}
              checked={value !== null && value !== undefined}
              onChange={(event) => onChange(event.target.checked ? defaults(schema) : null)}
            />
            Добавить {title.toLowerCase()}
          </label>
        )}
        {((value !== null && value !== undefined) || !optional) &&
          Object.entries(schema.properties).map(([nested, item]) => (
            <Input
              key={nested}
              id={`${id}.${nested}`}
              schema={item}
              value={data[nested]}
              required={Boolean(schema.required?.includes(nested))}
              choices={choices}
              parent={data}
              onChange={(current) => onChange({ ...data, [nested]: current })}
            />
          ))}
      </fieldset>
    );
  }
  if (type === 'array' && schema.items?.properties) {
    const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
    return (
      <fieldset className="form-section">
        <legend>{title}</legend>
        {rows.map((row, index) => (
          <div className="form-repeat" key={index}>
            {Object.entries(selectedSchema(schema.items!, row, true).properties || {}).map(
              ([nested, item]) => (
                <Input
                  key={nested}
                  id={`${id}.${index}.${nested}`}
                  schema={item}
                  value={row[nested]}
                  required={Boolean(selectedSchema(schema.items!, row).required?.includes(nested))}
                  choices={choices}
                  parent={row}
                  onChange={(current) =>
                    onChange(
                      rows.map((entry, i) =>
                        i === index
                          ? nested === schema.items!.discriminator
                            ? defaults(schema.items!, { ...entry, [nested]: current })
                            : { ...entry, [nested]: current }
                          : entry,
                      ),
                    )
                  }
                />
              ),
            )}
            <Button onClick={() => onChange(rows.filter((_, i) => i !== index))}>Удалить</Button>
          </div>
        ))}
        <Button onClick={() => onChange([...rows, defaults(schema.items!)])}>
          Добавить {title.toLowerCase().replace(/s$/, '')}
        </Button>
      </fieldset>
    );
  }
  if (type === 'boolean')
    return (
      <label className="form-check">
        <input
          type="checkbox"
          name={id}
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
        />
        {title}
      </label>
    );
  if (
    key === 'permission' ||
    key === 'dependencyKeys' ||
    path ||
    schema.format === 'uuid' ||
    schema.items?.format === 'uuid' ||
    schema.enum ||
    schema.items?.enum ||
    ['entityType', 'targetEntityType'].includes(key) ||
    choices[key]
  ) {
    const multiple = type === 'array';
    const items =
      schema.items?.enum ||
      schema.enum ||
      (['entityType', 'targetEntityType'].includes(key)
        ? [
            'Workspace',
            'Organization',
            'Department',
            'Team',
            'Employee',
            'Candidate',
            'Application',
            'JobPosting',
          ]
        : undefined);
    const vals =
      key === 'permission'
        ? permissionCodes.map((permission) => ({
            id: permission,
            title: permissionLabel(permission),
          }))
        : key === 'dependencyKeys'
          ? (choices.dependencies || []).map((row) => ({ id: row.id, title: name(row) }))
          : items
            ? items.map((item) => ({
                id: String(item),
                title: schema.enumLabels?.[String(item)] || human(String(item)),
              }))
            : options
                .map((row) => ({
                  id:
                    key === 'templateVersionId'
                      ? field(row, 'currentVersionId')
                      : [
                            'roleProfileVersionId',
                            'profileVersionId',
                            'skillVersionId',
                            'evaluationTemplateVersionId',
                          ].includes(key)
                        ? field(row, 'currentPublishedVersionId')
                        : row.id,
                  title:
                    name(row) ||
                    field(row, 'login') ||
                    field(row, 'code') ||
                    (row.versionNumber ? 'Версия ' + row.versionNumber : 'Текущее назначение'),
                }))
                .filter((row) => row.id);
    return (
      <Field label={title}>
        <ChoicePicker
          id={id}
          title={title}
          options={vals}
          value={value}
          multiple={multiple}
          optional={optional}
          onChange={onChange}
        />
      </Field>
    );
  }
  if (type === 'array')
    return (
      <Field label={title}>
        <textarea
          name={id}
          value={Array.isArray(value) ? value.join('\n') : ''}
          placeholder="Каждое значение с новой строки"
          onChange={(event) => onChange(event.target.value.split('\n').filter(Boolean))}
        />
      </Field>
    );
  if (type === 'object') return null;
  const text = String(value ?? '');
  const percent = schema.maximum === 10000 && schema.minimum === 0;
  const dateTimeText =
    schema.format === 'date-time' && text && /(Z|[+-]\d\d:\d\d)$/.test(text)
      ? new Date(new Date(text).getTime() - new Date(text).getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16)
      : text;
  const long = ['description', 'body', 'mission', 'comment', 'reason', 'hiringReason'].includes(
    key,
  );
  const inputType =
    schema.format === 'email'
      ? 'email'
      : schema.format === 'date'
        ? 'date'
        : schema.format === 'date-time'
          ? 'datetime-local'
          : ['integer', 'number'].includes(type || '')
            ? 'number'
            : 'text';
  return (
    <Field label={title + (percent ? ' (%)' : '')}>
      {long ? (
        <textarea
          name={id}
          required={required && !nullable(schema)}
          minLength={schema.minLength}
          maxLength={schema.maxLength}
          value={text}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          aria-label={title}
          name={id}
          type={inputType}
          required={required && !nullable(schema)}
          min={percent ? 0 : schema.minimum}
          max={percent ? 100 : schema.maximum}
          step={percent ? 0.01 : undefined}
          minLength={schema.minLength}
          maxLength={schema.maxLength}
          pattern={schema.pattern}
          value={
            percent
              ? text === ''
                ? ''
                : Number(text) / 100
              : schema.format === 'date-time'
                ? dateTimeText.slice(0, 16)
                : text
          }
          onChange={(event) =>
            onChange(
              percent && event.target.value !== ''
                ? Number(event.target.value) * 100
                : event.target.value,
            )
          }
        />
      )}
    </Field>
  );
}
export function actionSubmitLabel(action: Action) {
  if (action.method === 'PATCH' || action.method === 'PUT') return 'Сохранить';
  if (action.label.startsWith('Удалить')) return 'Удалить';
  if (action.label.startsWith('Восстановить')) return 'Восстановить';
  if (action.label.startsWith('Новая версия')) return 'Создать версию';
  if (action.label.startsWith('На согласование')) return 'Отправить';
  if (action.label.startsWith('Повторно пригласить')) return 'Пригласить';
  if (action.label.length > 22) {
    const verb = action.label.match(
      /^(Создать|Добавить|Назначить|Опубликовать|Зафиксировать|Отозвать|Завершить|Отменить|Отключить|Архивировать|Продлить|Обновить|Проверить|Настроить|Сохранить|Вывести|Заблокировать|Разблокировать|Приостановить|Возобновить|Отправить)/,
    )?.[1];
    if (verb) return verb;
  }
  return action.label;
}

export function ActionForm({
  action,
  onClose,
  onSuccess,
  presentation = 'page',
  submitLabel,
  navigation,
}: {
  action: Action;
  presentation?: 'page' | 'dialog';
  submitLabel?: string;
  navigation?: ReactNode;
  onClose: () => void;
  onSuccess: (result: Row) => void;
}) {
  const access = useAccess();
  const method = action.method || 'POST';
  const originalSchema: FormSchema =
    (operationSchemas as Record<string, FormSchema>)[`${method} ${action.path}`] || {};
  const baseSchema: FormSchema = action.schemaOverrides
    ? { ...originalSchema, properties: { ...originalSchema.properties, ...action.schemaOverrides } }
    : originalSchema;
  const employeeForm =
    action.path === '/api/v1/employees' ||
    action.path === '/api/v1/employees/{employeeId}/work-profile';
  const teamForm = action.path === '/api/v1/teams' || action.path === '/api/v1/teams/{teamId}';
  const initialRow: Row = {
    id: action.params?.employeeId || action.params?.teamId || '',
    ...action.initial,
  };
  const canArchive = employeeForm
    ? access.can?.('employees.archive', employeeAccessTargets(initialRow, access.data?.account))
    : teamForm
      ? access.can?.('projects.archive', organizationAccessTarget('teams', initialRow))
      : access.can?.('employees.archive');
  const permittedStatuses = (item: FormSchema, initial?: unknown): FormSchema => ({
    ...item,
    enum: item.enum?.filter(
      (value) =>
        (value !== 'OFFBOARDING' || initial === value) &&
        (value !== 'ARCHIVED' || canArchive || initial === value),
    ),
  });
  const schema: FormSchema = {
    ...baseSchema,
    properties: Object.fromEntries(
      Object.entries(baseSchema.properties || {}).map(([key, item]) => [
        key,
        (employeeForm || teamForm) && key === 'status'
          ? {
              ...permittedStatuses(item, action.initial?.status),
              enumLabels: employeeForm
                ? {
                    ...employeeStatusLabels,
                    OFFBOARDING: employeeStatusLabel('OFFBOARDING', true)!,
                  }
                : projectStatusLabels,
            }
          : action.path === '/api/v1/employees/bulk' && key === 'patch' && item.properties?.status
            ? {
                ...item,
                properties: {
                  ...item.properties,
                  status: {
                    ...permittedStatuses(item.properties.status),
                    enumLabels: employeeStatusLabels,
                  },
                },
              }
            : teamForm && key === 'startDate'
              ? { ...item, title: 'Дата начала' }
              : employeeForm && key === 'employmentType'
                ? {
                    ...item,
                    enum: [
                      ...new Set([
                        'FULL_TIME',
                        'PART_TIME',
                        'CONTRACTOR',
                        ...(action.initial?.employmentType
                          ? [String(action.initial.employmentType)]
                          : []),
                      ]),
                    ],
                  }
                : item,
      ]),
    ),
  };
  const [values, setValues] = useState(() => defaults(schema, action.initial));
  const countries = useMemo(
    () => (schema.properties?.countryCode ? countryChoices() : []),
    [Boolean(schema.properties?.countryCode)],
  );
  const timezones = useMemo(
    () =>
      schema.properties?.timezone
        ? timezoneChoices(String(values.countryCode || ''), String(values.timezone || ''))
        : [],
    [values.countryCode, values.timezone, Boolean(schema.properties?.timezone)],
  );
  const hiddenFields = [
    ...(action.hiddenFields || []),
    ...(['POST /api/v1/employees', 'PATCH /api/v1/employees/{employeeId}/work-profile'].includes(
      `${method} ${action.path}`,
    )
      ? [
          'employeeNumber',
          'jobFunctionId',
          'specialisationId',
          'functionalManagerId',
          'externalEmployee',
          'accessEndsAt',
          'buddyId',
          'contractEndDate',
          ...(schema.properties?.positionId ? ['jobTitle'] : []),
        ]
      : []),
    ...([
      '/api/v1/departments',
      '/api/v1/departments/{departmentId}',
      '/api/v1/teams',
      '/api/v1/teams/{teamId}',
      '/api/v1/positions',
      '/api/v1/positions/{positionId}',
    ].includes(action.path)
      ? ['code']
      : []),
    ...(action.path === '/api/v1/teams' || action.path === '/api/v1/teams/{teamId}'
      ? ['departmentId', 'managerEmployeeId', 'ownerEmployeeId']
      : []),
  ];
  const parameterSchemas: Record<string, FormSchema> =
    (operationParameters as Record<string, Record<string, FormSchema>>)[
      `${method} ${action.path}`
    ] || {};
  const [parameterValues, setParameterValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      Object.keys(parameterSchemas).map((key) => [
        key,
        action.params?.[key] ||
          (action.generatedParameters?.includes(key) ? crypto.randomUUID() : ''),
      ]),
    ),
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [receipt, setReceipt] = useState<Row | null>(null);
  const relations = [
    ...new Set([
      ...pathsFor({
        ...schema,
        properties: Object.fromEntries(
          Object.entries(schema.properties || {}).filter(([key]) => !hiddenFields.includes(key)),
        ),
      }),
      ...pathsFor({ properties: parameterSchemas }),
      ...targetPaths(values),
    ]),
  ].filter(
    (path) =>
      !action.choices?.[path] &&
      (permittedWorkAreas(access.data?.workAreas).includes('HR') ||
        ['/api/v1/accounts', '/api/v1/permission-groups'].includes(path)),
  );
  const queries = useQueries({
    queries: relations.map((path) => ({
      queryKey: ['choices', path],
      queryFn: ({ signal }: { signal: AbortSignal }) => allRows(path, {}, signal),
      staleTime: 30_000,
    })),
  });
  const stageQuery = useQuery({
    queryKey: ['stage-choices', action.params?.id],
    enabled: Boolean(schema.properties?.toStageId && action.params?.id),
    queryFn: ({ signal }) =>
      allRows('/api/v1/applications/{id}/pipeline', { id: action.params!.id! }, signal),
  });
  const assignmentQuery = useQuery({
    queryKey: ['assignment-choices', action.initial?.employeeId],
    enabled: Boolean(schema.properties?.assignmentId && action.initial?.employeeId),
    queryFn: ({ signal }) =>
      allRows(
        '/api/v1/employees/{id}/role-profile-assignments',
        { id: String(action.initial!.employeeId) },
        signal,
      ),
  });
  const basisCandidateId = String(
    values.candidateId ||
      (action.path.startsWith('/api/v1/candidates/{id}/') ? action.params?.id : '') ||
      '',
  );
  const basisQuery = useQuery({
    queryKey: ['basis-choices', basisCandidateId],
    enabled: Boolean(schema.properties?.processingBasisRecordId && basisCandidateId),
    queryFn: ({ signal }) =>
      allRows('/api/v1/candidates/{id}/processing-basis-records', { id: basisCandidateId }, signal),
  });
  const versionQuery = useQuery({
    queryKey: ['version-choices', action.path, action.params?.id],
    enabled: Boolean(schema.properties?.copyFromVersionId && action.params?.id),
    queryFn: ({ signal }) => allRows(action.path as GetPath, action.params, signal),
  });
  const choices = Object.fromEntries(
    relations.map((path, index) => [path, queries[index]?.data || []]),
  );
  Object.assign(choices, action.choices);
  if (schema.properties?.countryCode) choices.countryCode = countries;
  if (schema.properties?.timezone) choices.timezone = timezones;
  if (action.processingOptions) {
    const options = action.processingOptions;
    for (const key of ['source', 'purpose', 'jurisdiction'] as const) {
      const available = options.filter(
        (option) =>
          (key === 'source' || !values.source || option.source === values.source) &&
          (key !== 'jurisdiction' || !values.purpose || option.purpose === values.purpose),
      );
      choices[key] = [...new Set(available.map((option) => option[key]))].map((value) => ({
        id: value,
        name: key === 'source' ? human(value) : value,
      }));
    }
  }
  choices.workspace = access.data
    ? [{ id: access.data.account.workspaceId, name: 'Рабочее пространство' }]
    : [];
  if (stageQuery.data) choices.stages = stageQuery.data;
  if (assignmentQuery.data) choices.assignments = assignmentQuery.data;
  if (basisQuery.data)
    choices.processingBasisRecordId = basisQuery.data
      .filter(
        (row) =>
          row.status === 'ACTIVE' &&
          (!action.path.endsWith('/consents') || row.basis === 'CONSENT'),
      )
      .map((row) => ({
        ...row,
        name: `${field(row, 'purpose')} · ${field(row, 'jurisdiction')} · ${human(field(row, 'basis'))}`,
      }));
  if (versionQuery.data) choices.versions = versionQuery.data;
  choices.dependencies = (Array.isArray(values.stages) ? values.stages : []).flatMap((stage) =>
    Array.isArray((stage as Record<string, unknown>).tasks)
      ? (stage as { tasks: Record<string, unknown>[] }).tasks.map((task) => ({
          id: String(task.key),
          name: String(task.title || 'Новая задача'),
        }))
      : [],
  );
  const content = (
    <form
      className="domain-form"
      onSubmit={async (event) => {
        event.preventDefault();
        setPending(true);
        setError(null);
        try {
          const body = schema.properties ? formBody(schema, values) : undefined;
          for (const [key, item] of Object.entries(schema.properties || {}))
            if (
              item.minItems &&
              Array.isArray(body?.[key]) &&
              (body[key] as unknown[]).length < item.minItems
            )
              throw new Error(`Выберите минимум ${item.minItems}: ${label(key).toLowerCase()}`);
          const result = await command(
            method,
            action.path,
            body,
            Object.fromEntries(
              Object.entries(parameterValues).map(([key, value]) => [key, String(value)]),
            ),
          );
          if (typeof result.credential === 'string' || typeof result.actionLink === 'string')
            setReceipt(result);
          else onSuccess(result);
        } catch (cause) {
          setError(cause instanceof Error ? cause : new Error('Не удалось сохранить изменения'));
        } finally {
          setPending(false);
        }
      }}
    >
      {action.description && <p className="muted">{action.description}</p>}
      {Object.entries(parameterSchemas)
        .filter(([key]) => !action.params?.[key] && !action.generatedParameters?.includes(key))
        .map(([key, item]) => (
          <Input
            key={`path-${key}`}
            id={key}
            schema={item}
            value={parameterValues[key]}
            required
            choices={choices}
            parent={parameterValues}
            onChange={(value) => setParameterValues((previous) => ({ ...previous, [key]: value }))}
          />
        ))}
      {queries.some((query) => query.isLoading) ? (
        <Loading />
      ) : queries.some((query) => query.error) ? (
        <ErrorState error={queries.find((query) => query.error)?.error || null} />
      ) : (
        Object.entries(schema.properties || {})
          .filter(
            ([key]) =>
              !hiddenFields.includes(key) &&
              ![
                'version',
                'applicationVersion',
                'expectedEmployeeVersion',
                'submissionVersion',
              ].includes(key),
          )
          .map(([key, item]) => (
            <Input
              key={key}
              id={key}
              schema={
                action.processingOptions && ['source', 'purpose', 'jurisdiction'].includes(key)
                  ? { ...item, enum: undefined }
                  : item
              }
              value={values[key]}
              required={Boolean(schema.required?.includes(key))}
              choices={choices}
              parent={values}
              onChange={(current) =>
                setValues((previous) => ({
                  ...previous,
                  [key]: current,
                  ...(key === 'countryCode'
                    ? { timezone: defaultCountryTimezone(String(current || '')) }
                    : {}),
                  ...(key === 'entityType' ? { entityId: null } : {}),
                  ...(key === 'targetEntityType' ? { targetEntityId: null } : {}),
                  ...(key === 'scope' ? { scopeEntityId: null } : {}),
                  ...(key === 'organizationId'
                    ? { departmentId: null, primaryTeamId: null, additionalTeamIds: [] }
                    : {}),
                  ...(key === 'primaryTeamId'
                    ? {
                        additionalTeamIds: (Array.isArray(previous.additionalTeamIds)
                          ? previous.additionalTeamIds
                          : []
                        ).filter((id) => id !== current),
                      }
                    : {}),
                  ...(action.processingOptions && key === 'source'
                    ? { purpose: '', jurisdiction: '' }
                    : {}),
                  ...(action.processingOptions && key === 'purpose' ? { jurisdiction: '' } : {}),
                  ...(action.path === '/api/v1/accounts' && key === 'employeeId' && current
                    ? {
                        email: field(
                          choices['/api/v1/employees']?.find((row) => row.id === current),
                          'workEmail',
                        ),
                      }
                    : {}),
                }))
              }
            />
          ))
      )}
      {error && <ErrorState error={error} />}
      <footer>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          type="submit"
          className="primary"
          disabled={pending || queries.some((query) => query.isLoading || query.error)}
        >
          {pending ? 'Сохранение…' : submitLabel || actionSubmitLabel(action)}
        </Button>
      </footer>
    </form>
  );
  if (receipt)
    return (
      <section className="form-page">
        <div className="form-page-heading">
          <h1>{receipt.actionLink ? 'Ссылка для пользователя' : 'Ключ доступа'}</h1>
        </div>
        <div className="panel settings-card">
          <p>
            Сохраните {receipt.actionLink ? 'ссылку' : 'ключ'}. После закрытия страницы это значение
            нельзя получить повторно.
          </p>
          <Field
            label={receipt.actionLink ? 'Ссылка' : 'Ключ доступа'}
            name="credential"
            value={String(receipt.actionLink || receipt.credential)}
            readOnly
            autoComplete="off"
          />
          <Button
            className="primary"
            onClick={() => {
              const { credential, actionLink, ...record } = receipt;
              void credential;
              void actionLink;
              setReceipt(null);
              onSuccess(record as Row);
            }}
          >
            Готово
          </Button>
        </div>
      </section>
    );
  return presentation === 'dialog' ? (
    <Overlay title={action.label} variant="dialog" onClose={onClose}>
      {content}
    </Overlay>
  ) : (
    <section className="form-page">
      <div className="form-page-heading">
        <h1>{action.label}</h1>
      </div>
      {navigation}
      {content}
    </section>
  );
}
